import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Sparkle, CheckCircle, ArrowsClockwise, ShoppingCart, PaintBrush, FilePdf, DownloadSimple, MagnifyingGlassPlus, PencilSimple, X, Trash } from "@phosphor-icons/react";

export default function BriefDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [brief, setBrief] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [template, setTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [address, setAddress] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [zoomDesign, setZoomDesign] = useState(null);   // design shown in lightbox
  const [zoomScale, setZoomScale] = useState(1);
  const [editingDesign, setEditingDesign] = useState(null); // design being edited via AI (drawer form)
  const [editPrompt, setEditPrompt] = useState("");
  const [editLogoB64, setEditLogoB64] = useState(""); // optional new logo (base64, no data-URL prefix)
  const [editing, setEditing] = useState(false);
  const [zoomEditPrompt, setZoomEditPrompt] = useState(""); // inline edit bar inside zoom modal
  const [zoomEditing, setZoomEditing] = useState(false);

  const load = async () => {
    const [b, d] = await Promise.all([api.get(`/briefs/${id}`), api.get(`/briefs/${id}/designs`)]);
    setBrief(b.data); setDesigns(d.data);
    if (b.data.template_id) {
      try {
        const t = await api.get(`/templates?product_type=${b.data.product_type}`);
        setTemplate(t.data.find(x => x.id === b.data.template_id));
      } catch { /* template lookup optional */ }
    }
  };

  useEffect(() => { load(); }, [id]);

  // Fetch print-ready PDF (blob URL, keeps auth token in header) whenever brief loads.
  useEffect(() => {
    if (!brief) return;
    if (brief.design_source !== "template") return;
    let revokeUrl = null;
    (async () => {
      setPdfLoading(true);
      try {
        const res = await api.get(`/briefs/${brief.id}/print-pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        revokeUrl = url;
        setPdfUrl(url);
      } catch (e) {
        console.error("PDF preview failed", e);
      } finally { setPdfLoading(false); }
    })();
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [brief?.id, brief?.design_source, brief?.status]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/briefs/${id}/print-pdf?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `talaio-${id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const markReady = async () => {
    setMarkingReady(true);
    try {
      const { data } = await api.post(`/briefs/${id}/mark-ready`);
      toast.success("Print-ready! You can place your order now.");
      await load();
      // Fetch the newly-created placeholder design so approvedDesign is populated
      const d = await api.get(`/briefs/${id}/designs`);
      setDesigns(d.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark ready");
    } finally { setMarkingReady(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/briefs/${id}/generate`, { variations: 2 });
      setDesigns([...data, ...designs]);
      setBrief({ ...brief, status: "review" });
      toast.success(`Generated ${data.length} designs`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed. Please try again.");
    } finally { setGenerating(false); }
  };

  const regenerateWithEdits = async () => {
    if (!editPrompt.trim() && !editLogoB64) { toast.error("Describe what to change or upload a new logo"); return; }
    if (!editingDesign?.id) { toast.error("No design selected"); return; }
    setEditing(true);
    try {
      const { data } = await api.post(`/designs/${editingDesign.id}/edit`, {
        prompt: editPrompt.trim() || "Swap in the newly attached logo, keeping everything else the same.",
        new_logo_base64: editLogoB64 || null,
      });
      setDesigns((prev) => [data, ...prev]);
      toast.success("Edited version ready");
      setEditingDesign(null);
      setEditPrompt("");
      setEditLogoB64("");
      if (zoomDesign) { setZoomDesign(data); setZoomScale(1); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
    } finally { setEditing(false); }
  };

  const onEditLogoFileChosen = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Logo must be under 3 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = String(ev.target?.result || "");
      // Strip the data-URL prefix so we send raw base64 to the API
      const b64 = dataUrl.includes(",") ? dataUrl.split(",", 2)[1] : dataUrl;
      setEditLogoB64(b64);
    };
    reader.readAsDataURL(file);
  };

  const submitZoomEdit = async () => {
    if (!zoomEditPrompt.trim() || !zoomDesign) return;
    setZoomEditing(true);
    try {
      const { data } = await api.post(`/designs/${zoomDesign.id}/edit`, {
        prompt: zoomEditPrompt.trim(),
      });
      setDesigns((prev) => [data, ...prev]);
      setZoomDesign(data);
      setZoomScale(1);
      setZoomEditPrompt("");
      toast.success("Design updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
    } finally { setZoomEditing(false); }
  };

  const approve = async (designId) => {
    try {
      await api.post(`/designs/${designId}/approve`);
      toast.success("Design approved");
      load();
    } catch { toast.error("Approve failed"); }
  };

  const deleteDesign = async (designId) => {
    if (!window.confirm("Delete this design concept? This cannot be undone.")) return;
    try {
      await api.delete(`/designs/${designId}`);
      setDesigns((prev) => prev.filter((d) => d.id !== designId));
      if (zoomDesign?.id === designId) setZoomDesign(null);
      if (editingDesign?.id === designId) setEditingDesign(null);
      toast.success("Design deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const placeOrder = async () => {
    const approved = designs.find(d => d.approved);
    if (!approved) { toast.error("Approve a design first"); return; }
    if (!address.trim()) { toast.error("Delivery address required"); return; }
    setOrdering(true);
    try {
      const { data } = await api.post("/orders", {
        brief_id: id, design_id: approved.id, delivery_address: address,
      });
      toast.success("Order placed");
      nav(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally { setOrdering(false); }
  };

  if (!brief) return (<div className="min-h-screen bg-[var(--pf-bg)]"><Nav/><div className="p-12 pf-label">Loading…</div></div>);

  const approvedDesign = designs.find(d => d.approved);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10">
        <Link to="/dashboard" className="pf-label hover:text-[var(--pf-brand)]">← Dashboard</Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mt-6">
          {/* Left: brief summary */}
          <aside className="lg:col-span-4 border border-[var(--pf-border)] p-4 sm:p-6 h-fit lg:sticky lg:top-24">
            <div className="pf-label mb-2">Brief · #{brief.id.slice(0,8).toUpperCase()}</div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight break-words">{brief.title}</h1>
            <div className="mt-6 space-y-3 text-sm">
              {[
                ["Product", brief.product_type.replace('_',' ')],
                ["Industry", brief.industry],
                ["Size", brief.size],
                ["Quantity", brief.quantity.toLocaleString()],
                ["Style", brief.style],
                ["Colors", brief.color_scheme],
                ["Status", brief.status],
              ].map(([k,v]) => (
                <div key={k} className="flex justify-between border-b border-[var(--pf-border)] pb-2">
                  <span className="pf-label">{k}</span><span className="font-medium">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-sm text-[var(--pf-text-2)] whitespace-pre-wrap">{brief.description}</div>
          </aside>

          {/* Right: AI designs */}
          <section className="lg:col-span-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <div className="pf-label mb-2">
                  {brief.design_source === "human" ? "Designs from your designer" :
                   brief.design_source === "template" ? "Template concierge" : "AI Design Gallery"}
                </div>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
                  {brief.design_source === "template" ? "Your template order" : "Concepts"}
                </h2>
              </div>
              {brief.design_source === "ai" && (
                <button onClick={generate} disabled={generating} className="pf-btn-primary w-full sm:w-auto" data-testid="generate-designs">
                  {generating ? "Generating…" : (<>
                    {designs.length ? <ArrowsClockwise size={16}/> : <Sparkle size={16} weight="fill"/>}
                    {designs.length ? "Regenerate" : "Generate with AI"}
                  </>)}
                </button>
              )}
              {brief.design_source === "human" && (
                <div className="pf-label text-[var(--pf-brand)]">
                  {brief.status === "awaiting_designer" ? "⏳ Awaiting designer assignment" :
                   brief.status === "designing" ? "🎨 Designer is working on it" :
                   brief.status === "review" ? "✅ Design ready for review" : brief.status}
                </div>
              )}
              {brief.design_source === "template" && (
                <div className="pf-label text-[var(--pf-brand)]">
                  {brief.status === "template_pending" ? "🖨️ Auto-composited PDF ready" :
                   brief.status === "ready_for_print" ? "✅ Print-ready — approved" :
                   brief.status === "ordered" ? "📦 Ordered" :
                   brief.status === "review" ? "✅ Ready for review" : brief.status}
                </div>
              )}
            </div>

            {/* Template preview card */}
            {brief.design_source === "template" && brief.status !== "ready_for_print" && brief.status !== "ordered" && (
              <div className="border-2 border-[var(--pf-cyan)] bg-white rounded-2xl p-6 mb-6" data-testid="template-preview">
                <div className="flex items-start gap-6 flex-wrap">
                  {template && (
                    <div className="w-48 shrink-0">
                      <div className="rounded-xl overflow-hidden border border-[var(--pf-border)]">
                        <div className="aspect-[4/3] relative overflow-hidden" style={{ background: template.preview.bg }}>
                          <div className="absolute inset-3 border rounded" style={{ borderColor: template.preview.accent + '55' }}></div>
                          {brief.logo_base64 ? (
                            <img src={`data:image/png;base64,${brief.logo_base64}`} alt="logo"
                              className="absolute top-4 left-4 max-w-[80px] max-h-[40px] object-contain" />
                          ) : (
                            <div className="absolute top-3 left-3 text-[8px] tracking-[0.3em] font-mono font-bold" style={{ color: template.preview.fg, opacity: 0.7 }}>YOUR LOGO</div>
                          )}
                          <div className="absolute bottom-3 left-3" style={{ color: template.preview.fg }}>
                            <div className="text-sm font-black tracking-tight">{brief.extra_fields?.brand || brief.extra_fields?.project_name || brief.extra_fields?.restaurant_name || brief.extra_fields?.company || brief.title}</div>
                          </div>
                          <div className="absolute top-3 right-3 w-2 h-6" style={{ background: template.preview.accent }}></div>
                        </div>
                        <div className="p-3 bg-white">
                          <div className="font-bold text-sm">{template.name}</div>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-text-3)] mt-1">{template.style}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[var(--pf-cyan)]/20 text-[var(--pf-cyan)] flex items-center justify-center">
                        <FilePdf size={20} weight="duotone"/>
                      </div>
                      <div className="text-xl font-black tracking-tight">Auto-composited print file</div>
                    </div>
                    <p className="text-[var(--pf-text-2)]">
                      Your template has been composited with your logo and details into a{" "}
                      <strong className="text-[var(--pf-text)]">300 DPI, print-ready PDF with 3mm bleed and crop marks</strong>.
                      Preview it below, download the file, or approve and continue to order.
                    </p>
                    <ul className="mt-5 space-y-2 text-sm">
                      <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0"/> Template <strong>{template?.name || brief.template_id}</strong></li>
                      <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className={brief.logo_base64 ? "text-emerald-500 mt-0.5 shrink-0" : "text-[var(--pf-text-3)] mt-0.5 shrink-0"}/> {brief.logo_base64 ? "Logo embedded" : "No logo (text-only layout)"}</li>
                      <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0"/> {Object.values(brief.extra_fields || {}).filter(Boolean).length} custom fields laid out</li>
                    </ul>
                  </div>
                </div>

                {/* PDF preview + actions */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-start" data-testid="pdf-preview-block">
                  <div className="md:col-span-2 border border-[var(--pf-border)] rounded-lg overflow-hidden bg-[var(--pf-surface-2)]">
                    {pdfLoading && (
                      <div className="aspect-[3/2] flex items-center justify-center pf-label">Rendering PDF…</div>
                    )}
                    {!pdfLoading && pdfUrl && (
                      <iframe
                        src={pdfUrl}
                        title="Print-ready preview"
                        className="w-full h-[420px] bg-white"
                        data-testid="pdf-iframe"
                      />
                    )}
                    {!pdfLoading && !pdfUrl && (
                      <div className="aspect-[3/2] flex items-center justify-center pf-label">
                        Preview unavailable — try downloading the PDF.
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <button onClick={downloadPdf} className="pf-btn-secondary w-full" data-testid="download-pdf">
                      <DownloadSimple size={16}/> Download PDF
                    </button>
                    <button onClick={markReady} disabled={markingReady} className="pf-btn-primary w-full" data-testid="mark-ready">
                      {markingReady ? "Approving…" : <><CheckCircle size={16} weight="fill"/> Approve & Order</>}
                    </button>
                    <div className="text-xs text-[var(--pf-text-3)]">
                      300 DPI · CMYK-safe palette · 3mm bleed · crop marks
                    </div>
                  </div>
                </div>
              </div>
            )}

            {generating && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {[1,2].map(i => (
                  <div key={i} className="aspect-square border border-[var(--pf-border)] bg-[var(--pf-surface-2)] animate-pulse flex items-center justify-center">
                    <div className="pf-label">Rendering…</div>
                  </div>
                ))}
              </div>
            )}

            {!generating && designs.length === 0 && brief.design_source === "ai" && (
              <div className="border border-dashed border-[var(--pf-border)] p-16 text-center" data-testid="no-designs">
                <Sparkle size={40} className="mx-auto mb-4" />
                <p className="text-[var(--pf-text-2)] mb-2">No designs yet.</p>
                <p className="text-sm text-[var(--pf-text-2)]">Click Generate to create AI concepts based on your brief.</p>
              </div>
            )}
            {!generating && designs.length === 0 && brief.design_source === "human" && (
              <div className="border border-dashed border-[var(--pf-border)] p-16 text-center" data-testid="no-designs-human">
                <PaintBrush size={40} className="mx-auto mb-4 text-[var(--pf-brand)]" />
                <p className="text-[var(--pf-text-2)]">Your designer will deliver the final artwork here.<br/>You&apos;ll get a notification when it&apos;s ready.</p>
              </div>
            )}

            {designs.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {designs.map((d) => (
                  <div key={d.id} className={`border-2 p-3 transition-colors ${d.approved ? "border-[var(--pf-brand)]" : "border-[var(--pf-border)] hover:border-[var(--pf-text)]"}`} data-testid={`design-${d.id}`}>
                    <div
                      className={`relative aspect-square bg-[var(--pf-surface-2)] overflow-hidden flex items-center justify-center group ${d.image_base64 ? "cursor-zoom-in" : ""}`}
                      onClick={() => d.image_base64 && (setZoomDesign(d), setZoomScale(1))}
                      data-testid={`zoom-${d.id}`}
                    >
                      {d.image_base64 ? (
                        <>
                          <img src={`data:image/png;base64,${d.image_base64}`} alt="design" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-full text-xs font-bold uppercase tracking-widest">
                              <MagnifyingGlassPlus size={14} weight="bold"/> Zoom
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center px-4">
                          <FilePdf size={48} weight="duotone" className="mx-auto text-[var(--pf-cyan)]"/>
                          <div className="pf-label mt-3">Print-ready PDF</div>
                          <div className="text-xs text-[var(--pf-text-2)] mt-1">300 DPI · bleed · crop marks</div>
                        </div>
                      )}
                      {d.approved && (
                        <div className="absolute top-3 right-3 bg-[var(--pf-brand)] text-white px-3 py-1 text-xs uppercase tracking-widest font-bold pointer-events-none">Approved</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                      <div className="font-mono text-xs text-[var(--pf-text-2)]">#{d.id.slice(0,6).toUpperCase()}</div>
                      <div className="flex items-center gap-3">
                        {!d.image_base64 && (
                          <button onClick={downloadPdf} className="text-xs font-semibold uppercase tracking-wider hover:text-[var(--pf-brand)] flex items-center gap-1" data-testid={`download-design-${d.id}`}>
                            <DownloadSimple size={14}/> Download
                          </button>
                        )}
                        {d.image_base64 && (
                          <button
                            onClick={() => { setEditingDesign(d); setEditPrompt(""); }}
                            className="text-xs font-semibold uppercase tracking-wider hover:text-[var(--pf-brand)] flex items-center gap-1"
                            data-testid={`edit-${d.id}`}
                          >
                            <PencilSimple size={14}/> Edit
                          </button>
                        )}
                        {d.image_base64 && !d.approved && (
                          <button onClick={()=>approve(d.id)} data-testid={`approve-${d.id}`} className="text-xs font-semibold uppercase tracking-wider hover:text-[var(--pf-brand)] flex items-center gap-1">
                            <CheckCircle size={14}/> Approve
                          </button>
                        )}
                        <button
                          onClick={() => deleteDesign(d.id)}
                          data-testid={`delete-${d.id}`}
                          className="text-xs font-semibold uppercase tracking-wider text-[var(--pf-text-3)] hover:text-red-600 flex items-center gap-1"
                          title="Delete this concept"
                        >
                          <Trash size={14}/> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Order section */}
            {approvedDesign && (
              <div className="mt-10 border border-[var(--pf-text)] p-6" data-testid="order-section">
                <div className="pf-label mb-2">Ready to Print</div>
                <h3 className="text-2xl font-black tracking-tight mb-4">Place your order</h3>
                {!showOrder ? (
                  <button onClick={()=>setShowOrder(true)} className="pf-btn-primary" data-testid="show-order-form">
                    <ShoppingCart size={16}/> Order this design
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="pf-label block mb-2">Delivery address</label>
                      <textarea rows={3} className="pf-input" style={{padding:"0.75rem 0"}} data-testid="order-address"
                        value={address} onChange={(e)=>setAddress(e.target.value)} placeholder="Full delivery address" />
                    </div>
                    <button onClick={placeOrder} disabled={ordering} className="pf-btn-primary" data-testid="place-order">
                      {ordering ? "Placing…" : "Confirm & Place Order →"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Zoom Lightbox */}
      {zoomDesign && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 overflow-auto"
          onClick={() => !zoomEditing && setZoomDesign(null)}
          data-testid="zoom-lightbox"
        >
          {/* Inline "Ask AI to edit" bar at the top — leaves room for close button on the right */}
          <div
            className="absolute top-3 sm:top-4 left-3 right-16 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-20 sm:w-full sm:max-w-3xl sm:px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-full flex items-center pl-4 sm:pl-5 pr-1 sm:pr-2 py-2 gap-2 sm:gap-3 shadow-2xl">
              <Sparkle size={16} weight="fill" className="text-[var(--pf-brand)] shrink-0"/>
              <input
                type="text"
                value={zoomEditPrompt}
                onChange={(e) => setZoomEditPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !zoomEditing) submitZoomEdit(); }}
                placeholder="Ask AI to edit this design…"
                disabled={zoomEditing}
                className="flex-1 bg-transparent text-white placeholder-white/50 text-sm focus:outline-none min-w-0"
                data-testid="zoom-edit-input"
              />
              <button
                onClick={submitZoomEdit}
                disabled={zoomEditing || !zoomEditPrompt.trim()}
                className="pf-btn-primary py-2 px-3 sm:px-4 text-xs shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="zoom-edit-submit"
              >
                {zoomEditing ? "…" : (<><Sparkle size={14} weight="fill"/><span className="hidden sm:inline">Apply</span></>)}
              </button>
            </div>
            <div className="hidden sm:flex flex-wrap gap-2 mt-2 justify-center">
              {[
                "Change phone number to 9876543210",
                "Update email to hello@brand.com",
                "Make the brand name bigger and fully visible",
                "Change background to warm cream tones",
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setZoomEditPrompt(s)}
                  disabled={zoomEditing}
                  className="text-[11px] px-3 py-1 rounded-full bg-white/8 hover:bg-white/20 border border-white/10 text-white/80 hover:text-white transition disabled:opacity-40"
                  data-testid="zoom-edit-suggestion"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); setZoomDesign(null); }}
            className="absolute top-3 right-3 sm:top-6 sm:right-6 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
            data-testid="zoom-close"
            disabled={zoomEditing}
          >
            <X size={20} weight="bold"/>
          </button>
          <div
            className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-wrap items-center gap-1 sm:gap-2 bg-white/10 backdrop-blur px-2 sm:px-3 py-2 rounded-2xl sm:rounded-full text-white text-sm max-w-[95vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setZoomScale(s => Math.max(0.5, +(s - 0.25).toFixed(2)))} className="px-3 py-1 hover:bg-white/10 rounded" data-testid="zoom-out">−</button>
            <span className="font-mono w-14 text-center">{Math.round(zoomScale * 100)}%</span>
            <button onClick={() => setZoomScale(s => Math.min(4, +(s + 0.25).toFixed(2)))} className="px-3 py-1 hover:bg-white/10 rounded" data-testid="zoom-in">+</button>
            <button onClick={() => setZoomScale(1)} className="px-3 py-1 hover:bg-white/10 rounded text-xs uppercase tracking-widest" data-testid="zoom-reset">Reset</button>
            <a
              href={`data:image/png;base64,${zoomDesign.image_base64}`}
              download={`talaio-${zoomDesign.id.slice(0,8)}.png`}
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-1 hover:bg-white/10 rounded flex items-center gap-1 text-xs uppercase tracking-widest"
              data-testid="zoom-download"
            >
              <DownloadSimple size={14}/> Save
            </a>
            <button
              onClick={(e) => { e.stopPropagation(); deleteDesign(zoomDesign.id); }}
              className="px-3 py-1 hover:bg-red-500/30 rounded flex items-center gap-1 text-xs uppercase tracking-widest text-red-200"
              data-testid="zoom-delete"
              disabled={zoomEditing}
            >
              <Trash size={14}/> Delete
            </button>
          </div>
          <img
            src={`data:image/png;base64,${zoomDesign.image_base64}`}
            alt="Design zoomed"
            className={`max-w-full transition-transform duration-200 select-none mt-16 sm:mt-0 mb-16 sm:mb-0 ${zoomEditing ? "opacity-40" : ""}`}
            style={{ transform: `scale(${zoomScale})`, maxHeight: `${zoomScale * 60}vh`, cursor: zoomScale > 1 ? "move" : "zoom-in" }}
            onClick={(e) => { e.stopPropagation(); if (!zoomEditing) setZoomScale(s => s < 2 ? s + 0.5 : 1); }}
            data-testid="zoom-image"
          />
          {zoomEditing && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30" data-testid="zoom-edit-loading">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-5 text-white text-center">
                <Sparkle size={28} weight="fill" className="mx-auto mb-2 text-[var(--pf-brand)] animate-pulse"/>
                <div className="text-sm font-bold uppercase tracking-widest">Editing with AI</div>
                <div className="text-xs opacity-70 mt-1">Usually takes 10–20 seconds…</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit-with-AI drawer */}
      {editingDesign && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-2 sm:p-4"
          onClick={() => !editing && setEditingDesign(null)}
          data-testid="edit-drawer"
        >
          <div
            className="bg-white text-black rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6 border-b border-black/10 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="pf-label mb-1">Edit with AI</div>
                <h3 className="text-lg sm:text-2xl font-black tracking-tight">Describe what to change</h3>
                <p className="text-xs sm:text-sm text-black/60 mt-1">Your edit is applied on top of the original brief. We generate a fresh version.</p>
              </div>
              <button onClick={() => !editing && setEditingDesign(null)} className="w-9 h-9 shrink-0 rounded-full hover:bg-black/5 flex items-center justify-center" data-testid="edit-close">
                <X size={20}/>
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <img src={`data:image/png;base64,${editingDesign.image_base64}`} alt="current" className="w-full sm:w-32 h-40 sm:h-32 object-cover rounded-lg border border-black/10"/>
                <div className="flex-1 min-w-0">
                  <label className="pf-label block mb-2 text-black">What to change</label>
                  <textarea
                    rows={4}
                    className="w-full border border-black/15 rounded-lg p-3 text-sm focus:outline-none focus:border-black"
                    placeholder={'e.g. "Make the brand name RED SOUP fully visible and centered at the top."'}
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    data-testid="edit-prompt"
                    autoFocus
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[
                      "Make the brand name bigger and fully visible",
                      "Add my phone number at the bottom",
                      "Use warmer, more premium color palette",
                      "Simplify — less text, more whitespace",
                      "Blend the logo into the hero image with a subtle gradient scrim for legibility",
                      "Move the logo to the top-center and enlarge slightly",
                      "Remove the duplicate brand name text — logo alone is enough",
                      "Give the logo a subtle white circular backdrop for legibility",
                      "Let the hero photo fill the whole canvas — overlay the logo and headline on top",
                    ].map((p) => (
                      <button
                        key={p}
                        onClick={() => setEditPrompt(prev => prev ? `${prev}. ${p}` : p)}
                        className="text-xs px-3 py-1.5 rounded-full border border-black/15 hover:bg-black hover:text-white transition"
                        data-testid="edit-suggestion"
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  {/* NEW: optional logo swap */}
                  <div className="mt-4 pt-4 border-t border-black/10">
                    <label className="pf-label block mb-2 text-black">
                      {brief?.logo_base64 ? "Swap the logo (optional)" : "Add your logo (optional)"}
                    </label>
                    {!brief?.logo_base64 && !editLogoB64 && (
                      <p className="text-[11px] text-amber-800 bg-amber-50 border-l-2 border-amber-500 px-2 py-1.5 mb-3 rounded-sm" data-testid="edit-logo-missing-hint">
                        You didn&apos;t upload a logo when creating this brief. Attach one here and the AI will composite it into the design.
                      </p>
                    )}
                    {editLogoB64 ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={`data:image/png;base64,${editLogoB64}`}
                          alt="new logo"
                          className="w-14 h-14 object-contain rounded border border-black/15 bg-white p-1"
                          data-testid="edit-logo-preview"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-black/60">
                            {brief?.logo_base64 ? "New logo attached — the AI will swap it into your design." : "Logo attached — the AI will composite it into your design."}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEditLogoB64("")}
                          data-testid="edit-logo-clear"
                          className="text-xs uppercase tracking-widest font-bold text-rose-600 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 border-dashed border-black/25 hover:border-black rounded cursor-pointer transition-colors" data-testid="edit-logo-label">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="hidden"
                          data-testid="edit-logo-input"
                          onChange={(e) => onEditLogoFileChosen(e.target.files?.[0])}
                        />
                        {brief?.logo_base64 ? "+ Upload new logo" : "+ Upload your logo"}
                      </label>
                    )}
                    <p className="text-[11px] text-black/50 mt-2">PNG / JPG / SVG · max 3 MB · transparent background recommended.</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
                <button onClick={() => { if (!editing) { setEditingDesign(null); setEditLogoB64(""); setEditPrompt(""); } }} className="px-4 py-2 text-sm font-semibold uppercase tracking-widest hover:bg-black/5 rounded border border-black/10 sm:border-0" disabled={editing}>
                  Cancel
                </button>
                <button onClick={regenerateWithEdits} disabled={editing || (!editPrompt.trim() && !editLogoB64)} className="pf-btn-primary" data-testid="submit-edit">
                  {editing ? "Regenerating…" : (<><Sparkle size={16} weight="fill"/> Regenerate</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
